import { useEffect, useState } from 'react';
import type { Transition, Variants } from 'framer-motion';

function motionTime(seconds: number, reduced = false) {
  return reduced ? 0 : seconds;
}

function easeOutTransition(duration: number, reduced = false): Transition {
  return {
    duration: motionTime(duration, reduced),
    ease: 'easeOut',
  };
}

export function fadeIn(reduced = false): Variants {
  return {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: motionTime(0.3, reduced) } },
    exit: { opacity: 0, transition: { duration: motionTime(0.15, reduced) } },
  };
}

export function slideUp(reduced = false): Variants {
  return {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0, transition: easeOutTransition(0.3, reduced) },
    exit: { opacity: 0, y: 8, transition: { duration: motionTime(0.15, reduced) } },
  };
}

export function slideInRight(reduced = false): Variants {
  return {
    initial: { opacity: 0, x: 30 },
    animate: { opacity: 1, x: 0, transition: easeOutTransition(0.4, reduced) },
    exit: { opacity: 0, x: 20, transition: { duration: motionTime(0.15, reduced) } },
  };
}

export function scaleIn(reduced = false): Variants {
  return {
    initial: { opacity: 0, scale: 0.9 },
    animate: {
      opacity: 1,
      scale: 1,
      transition: reduced
        ? { duration: 0 }
        : {
            type: 'spring',
            stiffness: 260,
            damping: 20,
            mass: 0.8,
          },
    },
    exit: { opacity: 0, scale: 0.98, transition: { duration: motionTime(0.15, reduced) } },
  };
}

export function pageTransition(reduced = false): Variants {
  return {
    initial: { opacity: 0, y: 20 },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: motionTime(0.25, reduced),
        ease: 'easeOut',
      },
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: {
        duration: motionTime(0.15, reduced),
        ease: 'easeIn',
      },
    },
  };
}

export function staggerContainer(reduced = false): Variants {
  return {
    initial: {},
    animate: {
      transition: reduced
        ? { staggerChildren: 0, delayChildren: 0 }
        : { staggerChildren: 0.05, delayChildren: 0.1 },
    },
  };
}

export function staggerItem(reduced = false): Variants {
  return {
    initial: { opacity: 0, y: 15 },
    animate: {
      opacity: 1,
      y: 0,
      transition: easeOutTransition(0.25, reduced),
    },
  };
}

export function cardHover(reduced = false) {
  return reduced
    ? {}
    : {
        scale: 1.01,
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.22)',
      };
}

export function cardDrag(reduced = false) {
  return reduced
    ? { zIndex: 100 }
    : {
        scale: 1.03,
        zIndex: 100,
        boxShadow: '0 24px 48px rgba(0, 0, 0, 0.32)',
      };
}

export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(media.matches);
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return reduced;
}
